// 1. React Core Hooks
import { useEffect, useRef, useState } from "react"; // React hooks for lifecycle and state
// 2. WebSocket Libraries
import { Client } from "@stomp/stompjs"; // STOMP client for messaging over WebSockets
import SockJS from "sockjs-client"; // WebSocket fallback library for better browser support
// 3. Utilities / Services
import GetProxy from "./GetProxy"; // API proxy or HTTP service utility
import { decrypt, setLoggingOutWithWarning } from "./utilities/helpers"; // Utility function for decrypting data
// 4. Redux
import { useSelector } from "react-redux"; // Redux hook to access state
// 5. Custom Components
import Alert from "./common-components/Alert"; // Custom alert component for UI feedback
import PropTypes from "prop-types";

const proxy = GetProxy(false);

const WebSocketHandler = ({ onLogout }) => {
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState("warning");
  const onLogoutRef = useRef(onLogout);

  useEffect(() => {
    onLogoutRef.current = onLogout;
  }, [onLogout]);

  const { userName } = useSelector((state) => state.common.loggedUser);
  const encoded = decrypt(userName); // This is the current logged-in user ID

  useEffect(() => {
    if (!encoded) {
      return;
    } // If no user, skip subscription

    const stompClient = new Client({
      webSocketFactory: () => new SockJS(`${proxy}/websocket`),
      reconnectDelay: 5000,
      onConnect: () => {
        stompClient.subscribe(`/topic/user/${encoded}`, (message) => {
          const payload = JSON.parse(message.body);
          const { type, message: msg } = payload;

          if (type === "ROLE_UPDATED" || type === "USER_DISABLED") {
            setLoggingOutWithWarning(true);
            setAlertMessage(
              msg ||
                (type === "USER_DISABLED"
                  ? "User account has been disabled."
                  : "User role has been updated."),
            );
            setAlertType("warning");
            setShowAlert(true);

            // Delay logout so the user can read the alert for 5 seconds
            setTimeout(() => {
              setLoggingOutWithWarning(false);
              setShowAlert(false);
              if (onLogoutRef.current) {
                onLogoutRef.current(); // Logs out the user
              }
            }, 5000);
          }
        });
      },
      onStompError: (frame) => {
        console.error("STOMP Error:", frame.headers["message"]);
      },
    });

    stompClient.activate();

    return () => {
      stompClient.deactivate();
      setLoggingOutWithWarning(false);
    };
  }, [encoded]); // Re-run when logged-in user changes

  return (
    <>
      {showAlert && (
        <Alert alert={showAlert} type={alertType} message={alertMessage} />
      )}
    </>
  );
};

export default WebSocketHandler;
WebSocketHandler.propTypes = {
  onLogout: PropTypes.any.isRequired,
};
